import { Chart } from 'chart.js';
import { useEffect, useRef, useState } from 'react';
import MajorTestResultPdf from '../components/MajorTestResultPdf';
import { MajorTestKind, PdfHandle } from '../types';

const waitForImage = (image: HTMLImageElement) =>
  new Promise<void>(resolve => {
    if (image.complete) {
      resolve();
      return;
    }
    const finish = () => {
      image.removeEventListener('load', finish);
      image.removeEventListener('error', finish);
      clearTimeout(timeout);
      resolve();
    };
    const timeout = window.setTimeout(finish, 30000);
    image.addEventListener('load', finish);
    image.addEventListener('error', finish);
  });

function waitForPdfValue<T>(read: () => T | null, active: () => boolean) {
  return new Promise<T | null>((resolve, reject) => {
    const interval = window.setInterval(() => {
      const value = read();
      if (!active() || value) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve(active() ? value : null);
      }
    }, 50);
    const timeout = window.setTimeout(() => {
      clearInterval(interval);
      reject(new Error('Le document PDF ne peut pas être préparé.'));
    }, 30000);
  });
}

const useOnDemandPdf = (kind: MajorTestKind, resultId: number, myResult: any) => {
  const [requested, setRequested] = useState<{ key: string; result: any } | null>(null);
  const [preparingPdf, setPreparingPdf] = useState(false);
  const busy = useRef(false);
  const generation = useRef(0);
  const pdfRef = useRef<PdfHandle>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const key = `${kind}-${resultId}`;

  useEffect(() => {
    busy.current = false;
    setPreparingPdf(false);
    return () => {
      generation.current += 1;
    };
  }, [kind, resultId, myResult]);

  const downloadPdf = async () => {
    if (busy.current || !myResult) return;
    busy.current = true;
    const requestGeneration = generation.current;
    setPreparingPdf(true);
    setRequested({ key, result: myResult });

    try {
      // Let React mount the document, including asynchronous Needs pyramids and
      // the legacy PDF components' user lookup, before invoking their handle.
      const active = () => generation.current === requestGeneration;
      const root = await waitForPdfValue(() => {
        const documentRoot = rootRef.current;
        return documentRoot?.dataset.pdfKey === key &&
          pdfRef.current &&
          documentRoot.querySelector('img')
          ? documentRoot
          : null;
      }, active);
      if (!root) return;
      // Wait for the whole set concurrently; a slow image must not delay the
      // start of the next image request.
      const images = Array.from(root.querySelectorAll('img'));
      await Promise.all(images.map(waitForImage));
      if (rootRef.current !== root || generation.current !== requestGeneration) return;
      // A PDF mounted on demand can still be animating. The Harmony exporter
      // also reads the visible chart, so finish both charts before its snapshot.
      document.querySelectorAll('canvas').forEach(canvas => {
        const chart = Chart.getChart(canvas);
        if (chart) {
          chart.stop();
          chart.update('none');
        }
      });
      if (kind === 'harmony') {
        // The legacy exporter creates its chart snapshot asynchronously. Wait
        // for it, then keep the tall screen chart inside the PDF's 650px frame.
        const chartImage = await waitForPdfValue(
          () => root.querySelector<HTMLImageElement>('img[alt="Graphique des résultats"]'),
          active
        );
        if (!chartImage) return;
        chartImage.style.maxHeight = '650px';
        chartImage.style.objectFit = 'contain';
        if (!images.includes(chartImage)) await waitForImage(chartImage);
        if (!active()) return;
      }
      await pdfRef.current?.handleDownload();
    } finally {
      if (generation.current === requestGeneration) {
        busy.current = false;
        setPreparingPdf(false);
      }
    }
  };

  return {
    downloadPdf,
    preparingPdf,
    // Keep the requested document mounted: some legacy exporters return before
    // html2pdf has finished reading its images. A result change discards it.
    pdfElement:
      requested?.key === key && requested.result === myResult ? (
        <div ref={rootRef} data-pdf-key={key}>
          <MajorTestResultPdf kind={kind} myResult={myResult} resultId={resultId} pdfRef={pdfRef} />
        </div>
      ) : null,
  };
};

export default useOnDemandPdf;
